// src/pages/shipments/ShipmentsListPage/ShipmentsListPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ShipmentsAPI from '../../../api/v3/shipments-api-v3';

const BASE = '/app/sales/bagspa';

const STATUS_OPTIONS = [
  'pending',
  'ready_to_pack',
  'need_packing_vendor',
  'need_packing_customer',
  'waiting_customer_pickup',
  'send_to_vendor',
  'pick_up_by_customer',
  'delivered',
  'returned',
  'canceled'
];
const ROW_STATUS_OPTIONS = ['pending','in_progress','ready_to_pack'];
const TYPE_OPTIONS   = ['self_pickup','gojek','grab','jne','tiki'];
const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'Need Packing',
  ready_to_pack: 'Ready to BNK / Kaya',
  need_packing_vendor: 'Need Packing Vendor',
  need_packing_customer: 'Need Packing Customer',
  waiting_customer_pickup: 'Waiting Customer Pickup',
  send_to_vendor: 'Send to Vendor',
  pick_up_by_customer: 'Back to BNK / QC',
  delivered: 'Delivered',
  returned: 'Returned',
  canceled: 'Canceled',
};

function formatDate(d, withTime=false){
  if(!d) return '-';
  const dt = new Date(d);
  return withTime
    ? dt.toLocaleString('id-ID')
    : dt.toLocaleDateString('id-ID',{ day:'2-digit', month:'short', year:'numeric'});
}

const normVendor = (v) => (v || 'kaya').toString().toLowerCase();
const normEmail  = (e) => (e || '').toString().trim().toLowerCase();

function readyToVendorLabel(vendor) {
  return normVendor(vendor) === 'bnk' ? 'Ready to BNK' : 'Ready to Kaya';
}

function deriveWarehouseStatus(row) {
  if (!row || typeof row !== 'object') return row || '';

  const shipmentStatus = String(row?.status || '').toLowerCase();
  const orderStatus = String(row?.order_status || '').toLowerCase();
  const vendor = normVendor(row?.vendor);
  const hasPaid = !!(row?.is_full_paid || row?.has_dp_paid);

  if (['delivered', 'returned', 'canceled'].includes(shipmentStatus)) return shipmentStatus;

  if (shipmentStatus === 'waiting_customer_pickup') return 'waiting_customer_pickup';

  if (shipmentStatus === 'need_packing_customer' || orderStatus === 'need_packing_customer') {
    return 'need_packing_customer';
  }

  if (shipmentStatus === 'pick_up_by_customer' || orderStatus === 'customer_pick_up_process') {
    return 'pick_up_by_customer';
  }

  if (shipmentStatus === 'send_to_vendor' || orderStatus === 'vendor_processing') {
    return 'send_to_vendor';
  }

  if (shipmentStatus === 'ready_to_pack') return 'ready_to_pack';
  if (shipmentStatus === 'in_progress') return 'need_packing_vendor';

  // fallback untuk data lama:
  // shipment masih pending, tapi vendor kaya dan payment sudah masuk
  if (shipmentStatus === 'pending' && ['kaya', 'bnk'].includes(vendor) && hasPaid) {
    return 'ready_to_pack';
  }

  return shipmentStatus;
}

function shipmentStatusLabel(rowOrStatus) {
  const isRow = typeof rowOrStatus === 'object' && rowOrStatus !== null;

  const status = isRow
    ? deriveWarehouseStatus(rowOrStatus)
    : String(rowOrStatus || '').toLowerCase();

  if (status === 'ready_to_pack') {
    return isRow
      ? readyToVendorLabel(rowOrStatus?.vendor)
      : 'Ready to BNK / Kaya';
  }

  return STATUS_LABELS[status] || status || '-';
}

function shipmentStatusBadgeClass(rowOrStatus) {
  const status = typeof rowOrStatus === 'object'
    ? deriveWarehouseStatus(rowOrStatus)
    : String(rowOrStatus || '').toLowerCase();

  const map = {
    pending: 'badge-secondary',
    in_progress: 'badge-warning',
    ready_to_pack: 'badge-info',
    need_packing_vendor: 'badge-warning',
    need_packing_customer: 'badge-info',
    waiting_customer_pickup: 'badge-primary',
    send_to_vendor: 'badge-primary',
    pick_up_by_customer: 'badge-dark',
    delivered: 'badge-success',
    returned: 'badge-danger',
    canceled: 'badge-danger',
  };

  return map[status] || 'badge-light';
}

function canMarkBackToBNK(row) {
  return normVendor(row?.vendor) === 'kaya' && deriveWarehouseStatus(row) === 'send_to_vendor';
}

function canMarkDoneTreatmentBNK(row) {
  return normVendor(row?.vendor) === 'bnk' && deriveWarehouseStatus(row) === 'ready_to_pack';
}

function canMarkDelivered(row) {
  return deriveWarehouseStatus(row) === 'waiting_customer_pickup';
}

function safeFilename(input) {
  const s = (input || '').toString().trim();
  if (!s) return 'unknown';
  return s
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 80);
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function buildSkuSummary(items = []) {
  if (!Array.isArray(items) || items.length === 0) return '—';

  const skus = Array.from(
    new Set(
      items
        .map((it) => (it?.product_sku || '').toString().trim())
        .filter(Boolean)
    )
  );

  if (skus.length === 0) return '—';

  const visible = skus.slice(0, 3);
  const extra = skus.length > 3 ? ` (+${skus.length - 3} more)` : '';
  return visible.join('\n') + extra;
}

// Build a compact summary for items
function buildItemsSummary(items = []) {
  if (!Array.isArray(items) || items.length === 0) return '—';
  const lines = items.slice(0, 3).map((it) => {
    const bits = [
      it.product_sku || '-',
      [it.brand_name, it.category_name].filter(Boolean).join('/'),
      it.colour_name,
      (it.order_type || 'regular').toString().toLowerCase() === 'priority' ? 'Priority' : 'Regular',
      it.treatment_name || '-',
    ].filter(Boolean);
    return `${bits[0]} • ${bits[1]} • ${bits[2] || '-'} • ${bits[3]} • ${bits[4]}`;
  });

  const extra = items.length > 3 ? ` (+${items.length - 3} more)` : '';
  return lines.join('\n') + extra;
}

async function fetchPdfBlob(url, options = {}) {
  const res = await fetch(url, options);
  if (!res.ok) {
    let msg = `Failed to generate PDF (${res.status})`;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const j = await res.json();
        msg = j?.error || (Array.isArray(j?.errors) ? j.errors.join(', ') : msg);
      } else {
        const t = await res.text().catch(() => '');
        if (t) msg = `${msg}: ${t.slice(0, 160)}`;
      }
    } catch (_) {}
    throw new Error(msg);
  }
  return await res.blob();
}

async function fetchPdfBlobFirstOk(urls, options = {}) {
  let lastErr = null;
  for (const url of urls) {
    try {
      return await fetchPdfBlob(url, options);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Failed to generate PDF');
}

function downloadBlob(blob, filename) {
  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(a.href);
}

// Keep merge-key logic aligned with backend
function getCustomerEmail(row) {
  return normEmail(
    row?.customer?.email ||
    row?.customer_email ||       // NEW (backend)
    row?.order_email ||          // optional
    row?.order?.email            // optional
  );
}

function getCustomerId(row) {
  return (
    row?.customer?.id ||
    row?.customer_id ||          // NEW (backend)
    row?.order?.customer_id
  );
}

function tandaKeluarMergeKey(row) {
  const email = getCustomerEmail(row);
  if (email) return `email:${email}`;
  const cid = getCustomerId(row);
  if (cid) return `id:${cid}`;
  return 'unknown';
}

export default function ShipmentsListPage(){
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [filter, setFilter] = useState({
    q: '',
    status: '',
    shipment_type: '',
    schedule_mode: 'customer',
    from: '',
    to: ''
  });

  const [vendorKaya, setVendorKaya] = useState(true);
  const [vendorBNK,  setVendorBNK]  = useState(true);

  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);

  const [selectedIds, setSelectedIds] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [returningId, setReturningId] = useState(null);
  const [deliveringId, setDeliveringId] = useState(null);
  const [doneTreatmentId, setDoneTreatmentId] = useState(null);

  const [expanded, setExpanded] = useState({});

  const load = useCallback(async ()=>{
    try{
      setLoading(true);
      const params = {};

      const q = (filter.q || '').trim();
      if (q) params.q = q;

      if(filter.status) params.status_view = filter.status;
      if(filter.shipment_type) params.shipment_type = filter.shipment_type;

      if (filter.schedule_mode === 'customer') {
        if (filter.from) params.customer_from = filter.from;
        if (filter.to)   params.customer_to   = filter.to;
      } else if (filter.schedule_mode === 'vendor') {
        if (filter.from) params.vendor_from = filter.from;
        if (filter.to)   params.vendor_to   = filter.to;
      } else if (filter.schedule_mode === 'customer_rescheduled') {
        params.rescheduled_only = true;
        if (filter.from) params.customer_rescheduled_from = filter.from;
        if (filter.to)   params.customer_rescheduled_to   = filter.to;
      }

      const v = [];
      if (vendorKaya) v.push('kaya');
      if (vendorBNK)  v.push('bnk');
      if (v.length) params.vendor = v;
      params.hide_returned_to_store = true;

      const res  = await ShipmentsAPI.list(params);
      const data = res?.items || res || [];
      const nextRows = Array.isArray(data) ? data : [];
      setRows(nextRows);
      setSelectedIds(prev => prev.filter(id => nextRows.some(row => row.id === id)));
    }catch(e){
      setErr(e.message);
    }finally{
      setLoading(false);
    }
  }, [filter, vendorKaya, vendorBNK]);

  useEffect(() => {
  load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setPage(1); }, [filter, vendorKaya, vendorBNK]);

  const filtered = useMemo(() => rows, [rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const startIdx = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(startIdx, startIdx + per), [filtered, startIdx, per]);

  function toggleSelect(id){
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  }

  function toggleSelectAllVisible(checked){
    const visibleIds = paged.map(r=>r.id);
    setSelectedIds(prev => {
      if (checked) {
        const set = new Set([...prev, ...visibleIds]);
        return Array.from(set);
      } else {
        return prev.filter(id => !visibleIds.includes(id));
      }
    });
  }

  function toggleExpandRow(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  async function handleStatusChange(id,newStatus){
    if(!window.confirm(`Change status to "${shipmentStatusLabel(newStatus)}"?`)) return;
    try{
      await ShipmentsAPI.updateStatus(id, newStatus);
      await load();
    }catch(e){ alert(e.message); }
  }

  async function handleBackToBNK(id){
    if(!window.confirm('Mark this shipment as Back to BNK?')) return;
    try{
      setReturningId(id);
      await ShipmentsAPI.markReturnedToStore(id);
      setSelectedIds(prev => prev.filter(x => x !== id));
      await load();
    }catch(e){ alert(e.message); }
    finally{ setReturningId(null); }
  }

  async function handleDoneTreatmentBNK(id){
    if(!window.confirm('Mark treatment by BNK as done and continue to QC?')) return;
    try{
      setDoneTreatmentId(id);
      await ShipmentsAPI.markDoneTreatmentBNK(id);
      setSelectedIds(prev => prev.filter(x => x !== id));
      await load();
    }catch(e){ alert(e.message); }
    finally{ setDoneTreatmentId(null); }
  }

  async function handleDelivered(id){
    if(!window.confirm('Mark this shipment as Delivered?')) return;
    try{
      setDeliveringId(id);
      await ShipmentsAPI.updateStatus(id, 'delivered');
      await load();
    }catch(e){ alert(e.message); }
    finally{ setDeliveringId(null); }
  }

  const selectedRows = useMemo(
    () => rows.filter(r => selectedIds.includes(r.id)),
    [rows, selectedIds]
  );

  const selectedDoneTreatmentRow = useMemo(() => {
    if (selectedRows.length !== 1) return null;
    const row = selectedRows[0];
    return canMarkDoneTreatmentBNK(row) ? row : null;
  }, [selectedRows]);

  // Surat Jalan merge constraint (Kaya only)
  const canMergeSJ = selectedRows.length > 0 && selectedRows.every(s => normVendor(s.vendor) === 'kaya');
  const sjMergeTitle = canMergeSJ
    ? 'Merge Surat Jalan'
    : (selectedRows.length === 0
        ? 'Pilih minimal 1 shipment'
        : 'Hanya shipment vendor = Kaya yang bisa di-merge');

  // Tanda Keluar groups
  const tkGroups = useMemo(() => {
    const groups = new Map();
    for (const r of selectedRows) {
      const key = tandaKeluarMergeKey(r);
      if (!groups.has(key)) groups.set(key, { key, rows: [] });
      groups.get(key).rows.push(r);
    }
    return Array.from(groups.values());
  }, [selectedRows]);

  const tkGroupCount = tkGroups.length;
  const tkTitle =
    selectedRows.length === 0
      ? 'Pilih minimal 1 shipment'
      : `Auto-merge per customer: akan download ${tkGroupCount} PDF`;

  function resetFilters(){
    setFilter({
      q: '',
      status: '',
      shipment_type: '',
      schedule_mode: 'customer',
      from: '',
      to: ''
    });
    setVendorKaya(true);
    setVendorBNK(true);
  }

  async function bulkDownload(type){
    if(!selectedIds.length){ alert('Select at least one shipment first.'); return; }

    const API_BASE = process.env.REACT_APP_API_BASE;
    if (!API_BASE) {
      alert('Missing REACT_APP_API_BASE');
      return;
    }

    setDownloading(true);

    try{
      // ---------------------------
      // SURAT JALAN
      // ---------------------------
      if(type === 'surat_jalan'){
        if(!canMergeSJ){
          const offenders = selectedRows.filter(s => normVendor(s.vendor) !== 'kaya')
            .map(s => s.order_no || `#${s.id}`);
          alert(
            'Tidak bisa merge Surat Jalan.\n' +
            'Hanya shipment dengan vendor = "kaya" yang dapat di-merge.\n' +
            (offenders.length ? `Non-Kaya: ${offenders.join(', ')}` : '')
          );
          return;
        }

        const url = `${API_BASE}/api/v3/shipments/bulk_surat_jalan`;
        const blob = await fetchPdfBlob(url, {
          method: 'POST',
          headers: { 'Content-Type':'application/json' },
          body: JSON.stringify({ ids: selectedIds })
        });

        downloadBlob(blob, `Merged_Surat_Jalan_${new Date().toISOString().slice(0,10)}.pdf`);
        await load();
        alert('✅ Surat Jalan (Merged) download completed.');
        return;
      }

      // ---------------------------
      // TANDA KELUAR
      // ---------------------------
      if(type === 'tanda_keluar'){
        // Support BOTH possible routes:
        // 1) Correct: /api/v3/shipments/bulk_tanda_keluar
        // 2) Your current routes.rb bug: /api/v3/shipments/shipments/bulk_tanda_keluar
        const bulkUrls = [
          `${API_BASE}/api/v3/shipments/bulk_tanda_keluar`
          // `${API_BAS÷E}/api/v3/shipments/shipments/bulk_tanda_keluar`,
        ];

        const unknownGroup = tkGroups.find(g => g.key === 'unknown');
        if (unknownGroup) {
          alert('⚠️ Ada shipment yang customer/email-nya tidak terdeteksi (unknown). Untuk yang unknown akan fallback per-shipment.');
        }

        const failed = [];
        let mergedOk = 0;
        let fallbackCount = 0;

        for (const g of tkGroups) {
          const ids = g.rows.map(r => r.id);

          const customerName = g.rows?.[0]?.customer?.name || '';
          const customerEmail = getCustomerEmail(g.rows?.[0]) || '';
          const label = customerName || customerEmail || g.key;

          // unknown → fallback per shipment (rare)
          if (g.key === 'unknown') {
            for (const id of ids) {
              const singleUrl = `${API_BASE}/api/v3/shipments/${id}/tanda_keluar`;
              const blob = await fetchPdfBlob(singleUrl);
              downloadBlob(blob, `Tanda_Keluar_${id}.pdf`);
              await sleep(150);
            }
            fallbackCount += ids.length;
            continue;
          }

          // known customer → MUST merge (no fallback to avoid “many PDFs” experience)
          try {
            const blob = await fetchPdfBlobFirstOk(bulkUrls, {
              method: 'POST',
              headers: { 'Content-Type':'application/json' },
              body: JSON.stringify({ ids })
            });

            const fname = `Merged_Tanda_Keluar_${safeFilename(label)}_${new Date().toISOString().slice(0,10)}.pdf`;
            downloadBlob(blob, fname);
            mergedOk += 1;
            await sleep(250);
          } catch (e) {
            failed.push({ label, message: e?.message || 'Unknown error' });
          }
        }

        if (failed.length) {
          const lines = failed.slice(0, 5).map(f => `- ${f.label}: ${f.message}`).join('\n');
          alert(
            `❌ Ada ${failed.length} group yang gagal merge Tanda Keluar.\n\n` +
            `Contoh error:\n${lines}\n\n` +
            `NOTE: Tidak dilakukan fallback per-shipment untuk group yang sudah punya email (biar tidak download banyak file).`
          );
          return;
        }

        setSelectedIds([]);
        await load();
        alert(`✅ Tanda Keluar completed. Merged: ${mergedOk} PDF • Fallback(unknown): ${fallbackCount} file`);
        return;
      }

      alert('Unknown bulk type.');
    }catch(e){
      alert(`Error: ${e.message}`);
    }finally{
      setDownloading(false);
    }
  }

  const isRes = filter.schedule_mode === 'customer_rescheduled';
  const fromLabel = isRes ? 'Re-From' : 'From';
  const toLabel   = isRes ? 'Re-To'   : 'To';

  return (
    <div className="bagspa-page shipments-page">
      <div className="bagspa-page-header shipments-page-header">
        <h3 className="mb-0">📦 Shipments</h3>
      </div>

      {/* Filters */}
      <div className="bagspa-filter-card shipments-control-card">
        {/* TOP ROW */}
        <div className="shipments-control-top-row">
          <div className="bagspa-filter-field shipments-filter-search">
            <label className="form-label">Search</label>
            <input
              type="search"
              className="form-control"
              value={filter.q}
              placeholder="Cari order reference, SKU, nama atau customer..."
              onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') load(); }}
            />
          </div>

          <div className="bagspa-filter-field shipments-filter-status">
            <label className="form-label">Status</label>
            <select
              className="form-control"
              value={filter.status}
              onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{shipmentStatusLabel(s)}</option>
              ))}
            </select>
          </div>

          <div className="bagspa-filter-field shipments-filter-type">
            <label className="form-label">Type</label>
            <select
              className="form-control"
              value={filter.shipment_type}
              onChange={e => setFilter(f => ({ ...f, shipment_type: e.target.value }))}
            >
              <option value="">All</option>
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="shipments-date-group">
            <div className="bagspa-filter-field shipments-filter-from">
              <label className="form-label">{fromLabel}</label>
              <input
                type="date"
                className="form-control"
                value={filter.from}
                onChange={e => setFilter(f => ({ ...f, from: e.target.value }))}
              />
            </div>

            <div className="bagspa-filter-field shipments-filter-to">
              <label className="form-label">{toLabel}</label>
              <input
                type="date"
                className="form-control"
                value={filter.to}
                onChange={e => setFilter(f => ({ ...f, to: e.target.value }))}
              />
            </div>
          </div>

          <div className="bagspa-filter-field shipments-filter-schedule">
            <label className="form-label">Schedule</label>
            <select
              className="form-control"
              value={filter.schedule_mode}
              onChange={e => setFilter(f => ({ ...f, schedule_mode: e.target.value }))}
              title="Customer / Customer Rescheduled / Vendor"
            >
              <option value="customer">Customer</option>
              <option value="customer_rescheduled">Customer Rescheduled</option>
              <option value="vendor">Vendor</option>
            </select>
          </div>

          <div className="bagspa-filter-field shipments-filter-vendor">
            <label className="form-label">Vendor</label>
            <div className="bagspa-check-row shipments-vendor-row">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shipVdrKaya"
                  checked={vendorKaya}
                  onChange={(e) => setVendorKaya(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="shipVdrKaya">Kaya</label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shipVdrBNK"
                  checked={vendorBNK}
                  onChange={(e) => setVendorBNK(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="shipVdrBNK">BNK</label>
              </div>
            </div>
          </div>

          <div className="shipments-top-actions">
            <button className="btn btn-primary" onClick={load}>Apply</button>
            <button className="btn btn-outline-primary" onClick={resetFilters}>Reset</button>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="shipments-control-bottom-row">
          <div className="shipments-bottom-left">
            <div className="bagspa-filter-field shipments-filter-rows">
              <label className="form-label">Rows</label>
              <select
                className="form-control"
                value={per}
                onChange={(e) => {
                  setPer(parseInt(e.target.value, 10) || 25);
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}/page</option>
                ))}
              </select>
            </div>

            <button className="btn btn-secondary" onClick={load}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Instruction */}
      <div className="alert alert-secondary shipments-instruction-card" style={{ fontSize: '0.9rem' }}>
        <strong>Petunjuk:</strong> Status pada kolom <em>Status</em> tampil otomatis mengikuti flow bisnis. Untuk cetak massal, centang baris lalu gunakan tombol <em>BULK PRINT</em>. Print Surat Jalan vendor Kaya akan auto-update ke <em>Send to Vendor</em>.
        <br />
        <strong>Petunjuk Penggunaan:</strong>
        <ul className="mb-0 mt-1">
          <li>Gunakan <strong>Search</strong> untuk mencari shipment berdasarkan order reference, nama customer, atau SKU, tanpa harus mengetik lengkap.</li>
          <li>Kolom <strong>Status</strong> bersifat read-only dan berubah otomatis dari aksi bisnis.</li>
          <li>Untuk <strong>merge Surat Jalan</strong>, <code>vendor = "kaya"</code> saja yang bisa digabung.</li>
          <li>Untuk <strong>vendor BNK</strong>, gunakan tombol <strong>Done treatment by BNK</strong> saat treatment internal selesai untuk melanjutkan ke QC.</li>
          <li>Centang kolom <strong>Back to BNK</strong> saat barang sudah kembali ke store. Shipment akan hilang dari list vendor aktif dan order masuk ke QC.</li>
          <li>Setelah <strong>Bulk Tanda Keluar</strong>, status shipment otomatis menjadi <strong>Waiting Customer Pickup</strong>.</li>
          <li>Checklist <strong>Delivered</strong> hanya dicentang manual oleh tim WH saat barang benar-benar sudah dikirim / diambil customer.</li>
          <li><strong>Bulk Tanda Keluar</strong> auto-merge per customer berdasarkan email. Jika merge gagal, tidak fallback jadi banyak file kecuali customer unknown.</li>
        </ul>
      </div>

      {/* Selection Toolbar */}
      {selectedIds.length > 0 && (
        <div className="alert alert-info shipments-selection-toolbar">
          <span>
            {selectedIds.length} shipment(s) selected
            {downloading && <span className="ms-2 text-muted">(downloading…)</span>}
            {!downloading && (
              <span className="ms-2 text-muted">
                • TK groups: {tkGroupCount}
              </span>
            )}
          </span>

          <div className="shipments-selection-actions">
            {selectedDoneTreatmentRow && (
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleDoneTreatmentBNK(selectedDoneTreatmentRow.id)}
                disabled={doneTreatmentId === selectedDoneTreatmentRow.id || downloading}
                title="Trigger shipment BNK masuk ke QC existing"
              >
                {doneTreatmentId === selectedDoneTreatmentRow.id ? 'Processing...' : 'Done treatment by BNK'}
              </button>
            )}

            <button
              className="btn btn-sm btn-success"
              onClick={() => bulkDownload('surat_jalan')}
              disabled={!canMergeSJ || downloading}
              title={sjMergeTitle}
            >
              📄 Bulk Surat Jalan (Merged)
            </button>

            <button
              className="btn btn-sm btn-warning"
              onClick={() => bulkDownload('tanda_keluar')}
              disabled={downloading}
              title={tkTitle}
            >
              📑 Bulk Tanda Keluar (Auto-Merged)
            </button>
          </div>
        </div>
      )}

      {err && <div className="alert alert-danger">{err}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bagspa-table-card shipments-table-card">
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle shipments-table">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        paged.length > 0 &&
                        paged.every(r => selectedIds.includes(r.id))
                      }
                      onChange={e => toggleSelectAllVisible(e.target.checked)}
                    />
                  </th>
                  <th>#</th>
                  <th>Vendor</th>
                  <th>Order No</th>
                  <th>Customer</th>
                  <th>SKU</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Back to BNK</th>
                  <th>Delivered</th>
                  <th>City</th>
                  <th>Customer Scheduled</th>
                  <th>Customer Rescheduled</th>
                  <th>Vendor Scheduled</th>
                  <th>Updated</th>
                  <th>Items / Treatments</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paged.length > 0 ? paged.map((r, i) => (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelect(r.id)}
                          title={normVendor(r.vendor) !== 'kaya' ? 'Vendor BNK: tidak bisa ikut merge Surat Jalan' : undefined}
                        />
                      </td>

                      <td>{startIdx + i + 1}</td>

                      <td>
                        <span className={`badge ${normVendor(r.vendor) === 'kaya' ? 'badge-success' : 'badge-secondary'}`}>
                          {normVendor(r.vendor) === 'kaya' ? 'Kaya' : 'BNK'}
                        </span>
                      </td>

                      <td>{r.order_no || `ORD-${r.order_id}`}</td>
                      <td>{r.customer?.name || '-'}</td>

                      <td style={{ whiteSpace: 'pre-line', minWidth: 140 }}>
                        <span
                          className="text-muted small"
                          title={
                            Array.isArray(r.shipment_items)
                              ? r.shipment_items.map(it => it?.product_sku).filter(Boolean).join(', ')
                              : ''
                          }
                        >
                          {buildSkuSummary(r.shipment_items)}
                        </span>
                      </td>

                      <td>{r.shipment_type}</td>

                      <td>
                        <span
                          className={`badge ${shipmentStatusBadgeClass(r)}`}
                          style={{ fontSize: 13, padding: '8px 10px' }}
                        >
                          {shipmentStatusLabel(r)}
                        </span>
                      </td>

                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={!!r.returned_to_store_at}
                          disabled={returningId === r.id || !canMarkBackToBNK(r)}
                          onChange={() => handleBackToBNK(r.id)}
                          title={canMarkBackToBNK(r) ? 'Mark barang sudah balik ke store / BNK' : 'Available for Kaya shipments in Send to Vendor stage'}
                        />
                      </td>

                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={deriveWarehouseStatus(r) === 'delivered'}
                          disabled={deliveringId === r.id || !canMarkDelivered(r)}
                          onChange={() => handleDelivered(r.id)}
                          title={canMarkDelivered(r) ? 'Mark shipment as Delivered' : 'Available only when shipment is Waiting Customer Pickup'}
                        />
                      </td>

                      <td>{r.customer_address?.city || '-'}</td>
                      <td>{formatDate(r.customer_scheduled_at, true)}</td>
                      <td>{formatDate(r.customer_rescheduled_at, true)}</td>
                      <td>{formatDate(r.vendor_scheduled_at, true)}</td>
                      <td>{formatDate(r.updated_at, true)}</td>

                      <td style={{ whiteSpace: 'pre-line' }}>
                        <div className="shipments-items-cell">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => toggleExpandRow(r.id)}
                            title={expanded[r.id] ? 'Hide items' : 'Show items'}
                          >
                            {expanded[r.id] ? 'Hide' : 'Items'}
                          </button>

                          <span className="text-muted small">
                            {buildItemsSummary(r.shipment_items)}
                          </span>
                        </div>
                      </td>

                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => history.push(`${BASE}/shipments/${r.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>

                    {expanded[r.id] && (
                      <tr className="table-active">
                        <td colSpan={17}>
                          {Array.isArray(r.shipment_items) && r.shipment_items.length > 0 ? (
                            <div className="table-responsive shipments-nested-table-wrap">
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>SKU</th>
                                    <th>Brand</th>
                                    <th>Category</th>
                                    <th>Colour</th>
                                    <th>Order Type</th>
                                    <th>Treatment</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {r.shipment_items.map((it, idx) => (
                                    <tr key={it.id || idx}>
                                      <td>{idx + 1}</td>
                                      <td>{it.product_sku || '-'}</td>
                                      <td>{it.brand_name || '-'}</td>
                                      <td>{it.category_name || '-'}</td>
                                      <td>{it.colour_name || '-'}</td>
                                      <td>{(it.order_type || 'regular').toString().toLowerCase() === 'priority' ? 'Priority' : 'Regular'}</td>
                                      <td>{it.treatment_name || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <span className="text-muted">No items.</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )) : (
                  <tr>
                    <td colSpan={17} className="text-center text-muted">
                      No shipments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bagspa-pagination-row shipments-pagination-row">
            <div className="text-muted small">
              Total: {filtered.length} • Page {pageSafe} / {totalPages}
            </div>

            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                &rsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
